import React from "react";
import { toast } from "sonner";
import { FileText, Award, Download, Check } from "lucide-react";
import DashboardLayout from "@/components/DashboardLayout";
import PricingTiers from "@/components/PricingTiers";
import { downloadClientGuide, downloadMasterPrompt } from "@/utils/generateDocx";

export default function PlansGuide() {
  const dl = (fn, name) => toast.promise(fn(), { loading: `Generating ${name}…`, success: `${name} downloaded`, error: "Generation failed" });

  return (
    <DashboardLayout title="Plans & User Guide"
      actions={<div className="flex flex-wrap gap-2">
        <button onClick={() => dl(downloadClientGuide, "Client Guide")} className="btn-ghost px-4 py-2.5 rounded-full text-sm flex items-center gap-2" data-testid="download-client-guide-btn"><FileText size={16} /> Client Guide (.docx)</button>
        <button onClick={() => dl(downloadMasterPrompt, "Master Prompt Script")} className="btn-gold px-4 py-2.5 rounded-full text-sm flex items-center gap-2" data-testid="download-master-prompt-btn"><Download size={16} /> Master Prompt (.docx)</button>
      </div>}>
      <div className="fade-up">
        <div className="iw-card p-8 mb-8 flex items-start gap-4 border-[#D4AF37]/40">
          <Award className="text-[#D4AF37] shrink-0" size={28} strokeWidth={1.5} />
          <div>
            <h3 className="font-display text-2xl font-bold">Downloadable Documentation</h3>
            <p className="text-[#8B9BB4] mt-2">Export a formatted Word document onboarding manual for your clients, or the complete master prompt & development specification for this platform. Use the buttons above.</p>
          </div>
        </div>

        <p className="overline">Subscription Tiers</p>
        <h2 className="font-display text-3xl font-bold mt-1 mb-8">Compare plans</h2>
        <PricingTiers />

        <div className="mt-16">
          <p className="overline">Complete User Guide</p>
          <h2 className="font-display text-3xl font-bold mt-1 mb-3">Everything iWurks360 does</h2>
          <p className="text-[#8B9BB4] max-w-3xl mb-8">A digital-first networking platform that replaces paper cards with interactive, always-up-to-date smart cards — shareable by QR, NFC tap or link, and manageable across entire teams.</p>

          <div className="grid md:grid-cols-2 gap-4">
            {[
              ["Digital Card Builder", "Craft cards with a live mobile preview — photo, logo, bio, contacts, socials, products and a portfolio gallery."],
              ["AI Auto-Fill & Copywriter", "Paste a website or LinkedIn URL and Gemini AI builds the card and writes a polished bio for you."],
              ["QR & NFC Studio", "Export high-res QR codes and program physical NFC smart cards and badges."],
              ["Client Accounts & Seats", "Allocate seats to team members, enforce brand identity, and manage everyone centrally."],
              ["Bulk Import + Undo", "Onboard a whole team from a CSV with on-screen column mapping and one-click undo."],
              ["Self-Registration Portal", "Share a branded invite link so employees create their own cards — spam-protected."],
              ["Team Directory", "A searchable public directory with department filters and printable QR posters."],
              ["Lead Capture & CRM Webhooks", "Collect prospects on every card, export to CSV, and push leads to your CRM in real time."],
              ["Custom Domain & White-Label", "Serve cards under your own branded web address with SSL and no vendor badges."],
              ["Analytics & Audit Log", "Track views, saves and leads by card and department, with a full activity audit trail."],
            ].map(([t, d]) => (
              <div key={t} className="iw-card p-5" data-testid={`guide-feature-${t.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                <h3 className="font-display text-lg font-bold text-[#F6F1E4]">{t}</h3>
                <p className="text-sm text-[#8B9BB4] mt-1.5 leading-relaxed">{d}</p>
              </div>
            ))}
          </div>

          <h3 className="font-display text-2xl font-bold mt-12 mb-4">Benefits you gain by signing up</h3>
          <div className="iw-card p-6">
            <ul className="space-y-3">
              {[
                "Never reprint a card again — update once and every shared link, QR and NFC tag stays current.",
                "Win more business — turn every handshake into a captured lead that flows into your CRM.",
                "Protect your brand — enforce logos, colors and a custom domain across every employee card.",
                "Onboard teams in minutes — bulk import or self-registration instead of manual setup.",
                "Measure what matters — see which people and departments network and convert best.",
                "Go greener and look sharper — a premium, contactless, sustainable alternative to paper.",
              ].map((b) => (
                <li key={b} className="flex items-start gap-3 text-sm text-[#8B9BB4]">
                  <Check size={16} className="text-[#D4AF37] mt-0.5 shrink-0" strokeWidth={2} /> {b}
                </li>
              ))}
            </ul>
          </div>

          <h3 className="font-display text-2xl font-bold mt-12 mb-4">Getting started in 6 steps</h3>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              "Sign in and open Client Accounts.",
              "Create a client account with brand logo & color.",
              "Add your team via seats, CSV import or invite link.",
              "Customize cards — bio, portfolio, products, CTAs.",
              "Export QR/NFC and email signatures in the Studio.",
              "Connect a CRM webhook and watch Analytics.",
            ].map((s, i) => (
              <div key={s} className="iw-card p-5 flex gap-3">
                <span className="font-display text-2xl font-bold text-[#D4AF37]">{i + 1}</span>
                <p className="text-sm text-[#8B9BB4]">{s}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
